import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { lockedSignupDomainService, signupDomainService, ocipIpAllowlistService, setAuthToken } from '../services/apiService';

// New requests can arrive while the app sits open, so the counts are reloaded on this interval
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Pending approval counts, shared by the nav badge and the home page so they are loaded once rather than
// on every page. counts stays null until the first load succeeds, so "none waiting" can be told apart from
// "not loaded yet"; error only matters while counts is still null.
const PendingApprovalsContext = createContext({ counts: null, total: null, error: false, setCounts: () => {} });

export const PendingApprovalsProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [counts, setCountsState] = useState(null);
  const [error, setError] = useState(false);
  const accountId = accounts[0]?.homeAccountId;

  useEffect(() => {
    if (!accountId) {
      setCountsState(null);
      return undefined;
    }

    const load = async () => {
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ['api://7e9ba977-33b0-4e9c-ae75-55f65da317bd/access'],
          account: accounts[0],
        });
        setAuthToken(response.accessToken);
        const [signup, locked, ipAddresses] = await Promise.all([
          signupDomainService.listPending(),
          lockedSignupDomainService.listPending(),
          ocipIpAllowlistService.listPending(),
        ]);
        setCountsState({ signup: signup.data.length, locked: locked.data.length, ipAddresses: ipAddresses.data.length });
        setError(false);
      } catch (err) {
        console.error('Failed to load pending approvals', err);
        setError(true);
      }
    };

    load();
    const timer = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // The approvals page already holds every request, so it hands over fresh counts as decisions are made
  const setCounts = useCallback((next) => {
    setCountsState(next);
    setError(false);
  }, []);

  const total = counts ? counts.signup + counts.locked + counts.ipAddresses : null;

  return (
    <PendingApprovalsContext.Provider value={{ counts, total, error, setCounts }}>
      {children}
    </PendingApprovalsContext.Provider>
  );
};

export const usePendingApprovals = () => useContext(PendingApprovalsContext);
